import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  PointOfSale,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

import { useAuth } from "../../context/AuthContext";
import { cashService, inventoryService } from "../../services";
import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { emitCashSessionChanged } from "../../services/http/cashEvents";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

const EMPTY_FORM = {
  code: "",
  name: "",
  is_active: true,
};

export default function CashRegistersPage() {
  const isMobile = useMediaQuery("(max-width:768px)");
  const { isSuperuser, permissions = [], currentBranch } = useAuth();
  const branchId = currentBranch?.id || null;

  const canViewRegisters =
    isSuperuser ||
    permissions.includes("cash_register_view") ||
    permissions.includes("cash_register_manage");
  const canManageRegisters = isSuperuser || permissions.includes("cash_register_manage");
  const canOpenSession = isSuperuser || permissions.includes("cash_open") || permissions.includes("cash_session_manage");

  const [registers, setRegisters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedRegister, setSelectedRegister] = useState(null);
  const [openSessionDialogOpen, setOpenSessionDialogOpen] = useState(false);
  const [openSessionSubmitting, setOpenSessionSubmitting] = useState(false);
  const [openSessionRegister, setOpenSessionRegister] = useState(null);
  const [openSessionForm, setOpenSessionForm] = useState({
    opening_float: "0.00",
    opening_note: "",
  });
  const [activeGeneralInventory, setActiveGeneralInventory] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  
  const loadRegisters = useCallback(async () => {
    if (!branchId || !canViewRegisters) {
      setRegisters([]);
      return;
    }

    setLoading(true);
    try {
      const params = { branch_id: branchId };
      if (statusFilter === "active") params.active = "true";
      if (statusFilter === "inactive") params.active = "false";

      const data = await cashService.listRegisters(params);
      setRegisters(toArray(data));
    } catch {
      setRegisters([]);
      showSnack("No se pudieron cargar las cajas.", "error");
    } finally {
      setLoading(false);
    }
  }, [branchId, canViewRegisters, showSnack, statusFilter]);

  const loadOperationalLocks = useCallback(async () => {
    if (!branchId) {
      setActiveGeneralInventory(null);
      return;
    }
    try {
      const data = await inventoryService.getCountSettings({ branch_id: branchId });
      setActiveGeneralInventory(data?.active_general_inventory || null);
    } catch {
      setActiveGeneralInventory(null);
    }
  }, [branchId]);

  useEffect(() => {
    loadRegisters();
    loadOperationalLocks();
  }, [loadOperationalLocks, loadRegisters]);

  const openCreateDialog = () => {
    setForm(EMPTY_FORM);
    setCreateOpen(true);
  };

  const openEditDialog = useCallback((row) => {
    setSelectedRegister(row);
    setForm({
      code: row?.code || "",
      name: row?.name || "",
      is_active: Boolean(row?.is_active),
    });
    setEditOpen(true);
  }, []);

  const handleCreate = async () => {
    if (!branchId) return;
    if (!form.code.trim() || !form.name.trim()) {
      showSnack("Codigo y nombre son obligatorios.", "warning");
      return;
    }

    setSubmitting(true);
    try {
      await cashService.createRegister({
        branch_id: branchId,
        code: form.code.trim(),
        name: form.name.trim(),
        is_active: form.is_active,
      });
      setCreateOpen(false);
      showSnack("Caja creada correctamente.", "success");
      await loadRegisters();
    } catch {
      showSnack("No se pudo crear la caja.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!branchId || !selectedRegister?.id) return;
    if (!form.code.trim() || !form.name.trim()) {
      showSnack("Codigo y nombre son obligatorios.", "warning");
      return;
    }

    setSubmitting(true);
    try {
      await cashService.updateRegister(selectedRegister.id, {
        branch_id: branchId,
        code: form.code.trim(),
        name: form.name.trim(),
        is_active: form.is_active,
      });
      setEditOpen(false);
      setSelectedRegister(null);
      showSnack("Caja actualizada correctamente.", "success");
      await loadRegisters();
    } catch {
      showSnack("No se pudo actualizar la caja.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = useCallback(async (row) => {
    if (!branchId || !row?.id) return;
    try {
      await cashService.updateRegister(row.id, {
        branch_id: branchId,
        is_active: !Boolean(row.is_active),
      });
      showSnack(row.is_active ? "Caja desactivada." : "Caja activada.", "success");
      await loadRegisters();
    } catch {
      showSnack("No se pudo actualizar el estado de la caja.", "error");
    }
  }, [branchId, loadRegisters, showSnack]);

  const handleOpenSession = async () => {
    if (!branchId) return;
    if (activeGeneralInventory?.id) {
      showSnack("No puedes abrir caja durante inventario general.", "warning");
      return;
    }

    setOpenSessionSubmitting(true);
    try {
      await cashService.openSession({
        opening_float: openSessionForm.opening_float || "0",
        opening_note: openSessionForm.opening_note || "",
      });
      setOpenSessionDialogOpen(false);
      setOpenSessionRegister(null);
      setOpenSessionForm({ opening_float: "0.00", opening_note: "" });
      showSnack("Sesion de caja abierta correctamente.", "success");
      emitCashSessionChanged({ branchId, action: "open" });
      await loadRegisters();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409) {
        if (detail && !error?.response?.data?.session) {
          showSnack(detail, "error");
          await loadOperationalLocks();
          return;
        }
        setOpenSessionDialogOpen(false);
        setOpenSessionRegister(null);
        setOpenSessionForm({ opening_float: "0.00", opening_note: "" });
        showSnack("Ya existe una sesion OPEN.", "info");
        emitCashSessionChanged({ branchId, action: "open_existing" });
        await loadRegisters();
        return;
      }
      showSnack(detail || "No se pudo abrir la sesion de caja.", "error");
    } finally {
      setOpenSessionSubmitting(false);
    }
  };
  const activeGeneralInventoryId = activeGeneralInventory?.id || null;

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 90 },
      { field: "code", headerName: "Codigo", width: 150 },
      { field: "name", headerName: "Nombre", flex: 1, minWidth: 220 },
      {
        field: "is_active",
        headerName: "Estado",
        width: 130,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={value ? "Activa" : "Inactiva"}
            color={value ? "success" : "default"}
            variant={value ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "current_open_session_id",
        headerName: "Sesion actual",
        width: 160,
        renderCell: ({ value, row }) =>
          value ? (
            <Stack spacing={0.1}>
              <Chip size="small" color="warning" label={`OPEN #${value}`} />
              <Typography variant="caption" color="text.secondary">
                {formatDateTime(row.current_opened_at)}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Sin sesion OPEN
            </Typography>
          ),
      },
      {
        field: "created_at",
        headerName: "Creada",
        width: 170,
        renderCell: ({ value }) => (
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(value)}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "Acciones",
        width: 170,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.25}>
            {canOpenSession && row.is_active && !row.current_open_session_id && (
              <Tooltip title={activeGeneralInventoryId ? "No puedes abrir caja durante inventario general." : "Abrir sesion"}>
                <span>
                  <IconButton
                    size="small"
                    color="warning"
                    disabled={Boolean(activeGeneralInventoryId)}
                    onClick={() => {
                      setOpenSessionRegister(row);
                      setOpenSessionForm({ opening_float: "0.00", opening_note: "" });
                      setOpenSessionDialogOpen(true);
                    }}
                  >
                    <PlayCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Editar">
              <span>
                <IconButton size="small" disabled={!canManageRegisters} onClick={() => openEditDialog(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={row.is_active ? "Desactivar" : "Activar"}>
              <span>
                <IconButton
                  size="small"
                  color={row.is_active ? "error" : "success"}
                  disabled={!canManageRegisters}
                  onClick={() => handleToggleActive(row)}
                >
                  {row.is_active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [activeGeneralInventoryId, canManageRegisters, canOpenSession, handleToggleActive, openEditDialog]
  );

  if (!canViewRegisters) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para ver cajas.
        </Typography>
      </Box>
    );
  }

  if (!branchId) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sucursal no seleccionada
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Selecciona una sucursal para administrar cajas.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <MainText icon={PointOfSale} title={"Cajas"} subtitle={"Administracion de cajas por sucursal"} />

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refrescar">
            <IconButton
              onClick={() => {
                loadRegisters();
                loadOperationalLocks();
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManageRegisters && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
              Nueva caja
            </Button>
          )}
        </Stack>
      </Stack>

      {activeGeneralInventoryId ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          No puedes abrir caja durante inventario general.
        </Alert>
      ) : null}

      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            label="Estado"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="all">Todas</MenuItem>
            <MenuItem value="active">Activas</MenuItem>
            <MenuItem value="inactive">Inactivas</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ height: isMobile ? "calc(100vh - 250px)" : "calc(100vh - 290px)", width: "100%" }}>
        <DataGrid
          rows={registers}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          density={isMobile ? "compact" : "standard"}
          sx={{
            "& .MuiDataGrid-columnHeaders": { backgroundColor: "#10173c", color: "#ffffff" },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover":
              { backgroundColor: "#10173c !important" },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator":
              { color: "#ffffff !important" },
            "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(16, 23, 60, 0.08)" },
          }}
        />
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nueva caja</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
            <TextField
              label="Codigo"
              size="small"
              value={form.code}
              onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Nombre"
              size="small"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Activa
              </Typography>
              <Switch
                checked={Boolean(form.is_active)}
                onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={submitting}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editar caja</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
            <TextField
              label="Codigo"
              size="small"
              value={form.code}
              onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Nombre"
              size="small"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Activa
              </Typography>
              <Switch
                checked={Boolean(form.is_active)}
                onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleUpdate} disabled={submitting}>
            Guardar cambios
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openSessionDialogOpen} onClose={() => setOpenSessionDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Abrir sesion de caja</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Caja: {openSessionRegister?.code} - {openSessionRegister?.name}
            </Typography>
            <TextField
              label="Fondo inicial"
              size="small"
              type="number"
              value={openSessionForm.opening_float}
              onChange={(event) =>
                setOpenSessionForm((prev) => ({ ...prev, opening_float: event.target.value }))
              }
              inputProps={{ min: 0, step: "0.01" }}
              fullWidth
            />
            <TextField
              label="Nota de apertura"
              size="small"
              multiline
              minRows={2}
              value={openSessionForm.opening_note}
              onChange={(event) =>
                setOpenSessionForm((prev) => ({ ...prev, opening_note: event.target.value }))
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSessionDialogOpen(false)} disabled={openSessionSubmitting}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleOpenSession} disabled={openSessionSubmitting}>
            Abrir caja
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}


